import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckInHeader from '../../components/check-in/CheckInHeader';
import { useCheckInCategories } from '../../hooks/useCheckIn';
import { useCreateMoodCheckIn } from '../../hooks/useMoodCheckIns';
import { categories as categoryData } from '../../data/check-in';
import { containerVariants, itemVariants } from '../../lib/animations';
import type { MoodEmotion } from '../../types/mood-check-in.types';
import { useAnalytics } from '../../contexts/AnalyticsContext';

// The 4 weather quadrants in display order
const quadrants = [
  { label: 'sunny' },
  { label: 'hazy' },
  { label: 'rainy' },
  { label: 'stormy' },
];

// Compute outer-corner rounding based on actual grid position after filtering
function getCornerClasses(index: number, count: number): string {
  const cols = count >= 2 ? 2 : 1;
  const rows = Math.ceil(count / cols);
  const row = Math.floor(index / cols);
  const col = index % cols;
  const isFirstRow = row === 0;
  const isLastRow = row === rows - 1;
  const isFirstCol = col === 0;
  const isLastCol = col === cols - 1 || index === count - 1;

  const classes: string[] = [];
  if (isFirstRow && isFirstCol) classes.push('rounded-tl-[48px]');
  if (isFirstRow && isLastCol) classes.push('rounded-tr-[48px]');
  if (isLastRow && isFirstCol) classes.push('rounded-bl-[48px]');
  if (isLastRow && isLastCol) classes.push('rounded-br-[48px]');
  return classes.join(' ');
}

export default function CheckInWeatherPage() {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { data: visibleCategories, isLoading } = useCheckInCategories();
  const { mutate: createMoodCheckIn } = useCreateMoodCheckIn();

  const handleWeatherSelect = (emotion: MoodEmotion) => {
    track('category_select', { category: emotion });
    // Fire-and-forget: record mood in background
    createMoodCheckIn({ emotion });
    // Navigate immediately (don't wait for API)
    navigate(`/check-in/${emotion}`);
  };

  // Set of visible category labels from the API
  const visibleLabels = new Set(visibleCategories?.map((c) => c.label) ?? []);

  return (
    <div className="min-h-screen flex flex-col bg-[#58C5DA]">
      <CheckInHeader variant="compact" />

      <motion.div
        className="flex-1 flex flex-col items-center px-6 mt-4 w-full max-w-[430px] mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Question text */}
        <motion.p
          className="text-center text-[#1B2831] font-semibold text-2xl md:text-[26px] lg:text-[28px] max-w-[344px] mt-8"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-0.96px' }}
          variants={itemVariants}
        >
          How are you feeling today?
        </motion.p>

        {/* 2x2 Weather Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 w-full max-w-[430px] gap-0">
            {quadrants.map((q, i) => (
              <div
                key={q.label}
                className={`${getCornerClasses(i, quadrants.length)} bg-[#58C5DA]/50 animate-pulse py-14`}
              />
            ))}
          </div>
        ) : (
          <motion.div className="grid grid-cols-2 w-full max-w-[430px] gap-0" variants={itemVariants}>
            {(() => {
              const visible = quadrants.filter((q) => visibleLabels.has(q.label));
              return visible.map((q, i) => {
                const data = categoryData[q.label];
                if (!data) return null;

                return (
                  <div
                    key={q.label}
                    className={`${getCornerClasses(i, visible.length)} flex flex-col items-center justify-center py-8 px-4 hover:bg-white/10 transition-colors cursor-pointer`}
                    onClick={() => handleWeatherSelect(q.label as MoodEmotion)}
                  >
                    <img
                      src={data.cloudAsset}
                      alt={data.name}
                      className="w-[102px] max-w-full"
                    />
                    <span
                      className="text-xs font-black uppercase text-[#1B2831] mt-2"
                      style={{ fontFamily: 'DM Sans', letterSpacing: '0.72px' }}
                    >
                      {q.label}
                    </span>
                    <span
                      className="text-[12px] font-normal text-[#1B2831] text-center leading-tight"
                      style={{ fontFamily: 'DM Sans', letterSpacing: '0.3px' }}
                    >
                      {data.descriptor}
                    </span>
                  </div>
                );
              });
            })()}
          </motion.div>
        )}

        {/* Instruction text */}
        <motion.p
          className="text-center text-[#1B2831] text-xl max-w-[344px] mt-6"
          style={{ fontFamily: 'DM Sans', letterSpacing: '-0.8px' }}
          variants={itemVariants}
        >
          Take turns! Tap the cloud for whoever&apos;s checking in first.
        </motion.p>


      </motion.div>
    </div>
  );
}
